import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Modal,
  Pagination,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";
import {
  OmsProduct,
  OmsReport,
  OmsReportPart,
  Stage,
} from "../../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import useOmsReport from "../../hooks/useOmsReport";
import "./omsReportDownload.scss";

function fileSize(bytes: number): string {
  const unit = [
    { bytes: 1_000_000_000_000, label: "TB" },
    { bytes: 1_000_000_000, label: "GB" },
    { bytes: 1_000_000, label: "MB" },
    { bytes: 1_000, label: "kB" },
  ].find((candidate) => bytes >= candidate.bytes);
  if (!unit) return `${bytes.toLocaleString()} B`;
  return `${(bytes / unit.bytes).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} ${unit.label}`;
}

function refreshedAt(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

function ReportSummary({
  report,
  showFiles = true,
}: {
  report: OmsReport;
  showFiles?: boolean;
}) {
  return (
    <dl className="oms-report-download__summary">
      <div>
        <dt>Data refreshed</dt>
        <dd>
          <time dateTime={report.sourceRefreshedAt}>
            {refreshedAt(report.sourceRefreshedAt)}
          </time>
        </dd>
      </div>
      <div>
        <dt>Rows</dt>
        <dd>{report.rowCount.toLocaleString()}</dd>
      </div>
      <div>
        <dt>Total size</dt>
        <dd>{fileSize(report.totalBytes)}</dd>
      </div>
      {showFiles && (
        <div>
          <dt>CSV files</dt>
          <dd>{report.parts.length.toLocaleString()}</dd>
        </div>
      )}
    </dl>
  );
}

function unavailableMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 404) return "No completed scheduled report is available yet.";
  if (status === 403) return "You do not have access to download this report.";
  return "The report is temporarily unavailable. Refresh to try again.";
}

const PAGE_SIZE = 25;

export default function OMSReportDownload({
  product,
}: {
  product: OmsProduct;
}) {
  const query = useOmsReport(product);
  const [snapshot, setSnapshot] = useState<OmsReport | null>(null);
  const [page, setPage] = useState(1);
  const [requestedPart, setRequestedPart] = useState<string | null>(null);
  const productLabel = {
    [OmsProduct.Channels]: "PV Channels",
    [OmsProduct.Addon]: "PV Addon",
    [OmsProduct.Tvod]: "PV TVOD",
  }[product];
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const download = useMutation({
    mutationFn: async (part: OmsReportPart) => {
      if (!snapshot) throw new Error("Select a report first");
      return (
        await OmsReportsApiFactory().getOmsReportPartDownload(
          product,
          Stage.Prod,
          snapshot.snapshotId,
          part.id
        )
      ).data;
    },
    onSuccess: (result, part) => {
      if (mounted.current) {
        setRequestedPart(part.id);
        window.location.assign(result.url);
      }
    },
  });
  const firstPart = (page - 1) * PAGE_SIZE;
  const visibleParts = snapshot?.parts.slice(firstPart, firstPart + PAGE_SIZE);

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            description="Includes the complete scheduled report for this product. Dashboard filters do not affect this download."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="refresh"
                  ariaLabel="Refresh scheduled report"
                  loading={query.isFetching}
                  loadingText="Refreshing scheduled report"
                  wrapText={false}
                  onClick={() => {
                    query.refetch().catch(() => undefined);
                  }}
                />
                <Button
                  variant="primary"
                  iconName="download"
                  wrapText={false}
                  ariaHaspopup="dialog"
                  disabled={!query.data || query.isFetching || query.isError}
                  onClick={() => {
                    setSnapshot(query.data || null);
                    setPage(1);
                    setRequestedPart(null);
                    download.reset();
                  }}
                >
                  Download full report
                </Button>
              </SpaceBetween>
            }
          >
            Scheduled report
          </Header>
        }
      >
        <SpaceBetween size="xs">
          {query.isLoading && (
            <div className="oms-report-download__status" role="status">
              <Spinner />
              <span>Loading scheduled report…</span>
            </div>
          )}
          {query.isError && (
            <Alert
              type={
                (query.error as { response?: { status?: number } })?.response
                  ?.status === 404
                  ? "info"
                  : "warning"
              }
              header="Report unavailable"
            >
              {unavailableMessage(query.error)}
            </Alert>
          )}
          {query.data && !query.isError && (
            <ReportSummary report={query.data} />
          )}
          {query.isFetching && !query.isLoading && (
            <div role="status">
              <Box color="text-body-secondary">Refreshing report details…</Box>
            </div>
          )}
        </SpaceBetween>
      </Container>
      <Modal
        visible={snapshot !== null}
        onDismiss={() => setSnapshot(null)}
        header={`Download ${productLabel} report`}
        size="large"
        closeAriaLabel="Close report download"
        footer={
          <Box float="right">
            <Button wrapText={false} onClick={() => setSnapshot(null)}>
              Close
            </Button>
          </Box>
        }
      >
        {snapshot && (
          <SpaceBetween size="m">
            <Box>
              {snapshot.parts.length > 1
                ? `Download all ${snapshot.parts.length.toLocaleString()} CSV files for the complete report.`
                : "Download the CSV file for the complete report."}{" "}
              Each file includes column headers.
            </Box>
            <ReportSummary report={snapshot} showFiles={false} />
            {snapshot.rowCount === 0 && (
              <Alert type="info">
                This report is empty. The CSV contains column headers.
              </Alert>
            )}
            {download.isError && (
              <Alert
                type="error"
                header={
                  download.variables
                    ? `Could not prepare file ${download.variables.id}`
                    : "Could not prepare file"
                }
              >
                Your download did not start. Choose Download again to retry.
              </Alert>
            )}
            {(download.isLoading || (requestedPart && !download.isError)) && (
              <div role="status" aria-live="polite" aria-atomic="true">
                {download.isLoading ? (
                  <Box color="text-body-secondary">
                    Preparing file {download.variables?.id}…
                  </Box>
                ) : (
                  <Box color="text-body-secondary">
                    File {requestedPart} was sent to your browser. Check your
                    browser’s downloads for progress.
                  </Box>
                )}
              </div>
            )}
            {snapshot.parts.length > PAGE_SIZE && (
              <div className="oms-report-download__pagination">
                <Box color="text-body-secondary">
                  Files {(firstPart + 1).toLocaleString()}–
                  {Math.min(
                    firstPart + PAGE_SIZE,
                    snapshot.parts.length
                  ).toLocaleString()}{" "}
                  of {snapshot.parts.length.toLocaleString()}
                </Box>
                <Pagination
                  currentPageIndex={page}
                  pagesCount={Math.ceil(snapshot.parts.length / PAGE_SIZE)}
                  ariaLabels={{
                    nextPageLabel: "Next page of CSV files",
                    previousPageLabel: "Previous page of CSV files",
                    pageLabel: (pageNumber) => `CSV files page ${pageNumber}`,
                  }}
                  onChange={({ detail }) => setPage(detail.currentPageIndex)}
                />
              </div>
            )}
            <ul
              className="oms-report-download__files"
              aria-label={`${productLabel} CSV files`}
            >
              {visibleParts?.map((part) => (
                <li className="oms-report-download__file" key={part.id}>
                  <div className="oms-report-download__file-name">
                    <Box fontWeight="bold">File {part.id}</Box>
                    <Box fontSize="body-s" color="text-body-secondary">
                      <span className="oms-report-download__filename">
                        {part.fileName}
                      </span>
                    </Box>
                  </div>
                  <dl className="oms-report-download__file-details">
                    <div>
                      <dt>Rows</dt>
                      <dd>{part.rowCount.toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt>Size</dt>
                      <dd>{fileSize(part.sizeBytes)}</dd>
                    </div>
                  </dl>
                  <div className="oms-report-download__file-action">
                    <Button
                      ariaLabel={`Download CSV file ${part.id}`}
                      iconName="download"
                      wrapText={false}
                      fullWidth
                      loadingText={`Preparing CSV file ${part.id}`}
                      loading={
                        download.isLoading && download.variables?.id === part.id
                      }
                      disabled={
                        download.isLoading && download.variables?.id !== part.id
                      }
                      onClick={() => {
                        setRequestedPart(null);
                        download.mutate(part);
                      }}
                    >
                      Download
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </SpaceBetween>
        )}
      </Modal>
    </>
  );
}
